import { cva, type VariantProps } from 'class-variance-authority';
import { uiMarker, uiMarkerLayout } from './shared';

/**
 * STAT CARD
 *
 * One headline number on a card: a label, the figure, and how it moved.
 * The analytics page drew four of these by hand as a Card with a Stack in it;
 * the dashboard overviews want the same tile for calls, revenue and drivers.
 *
 * The delta's colour comes from whether the movement is *good*, not from
 * whether it went up — a bounce rate falling is the one an owner wants to
 * see, and painting it red because the arrow points down reads as a warning.
 * A movement with no good/bad reading stays muted rather than picking a side.
 */
export const statCardRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col gap-2',
]);

/** Colour only — the row itself is an Inline. */
export const statCardDeltaRecipe = cva([uiMarker], {
  variants: {
    trend: {
      good: 'text-success',
      bad: 'text-destructive',
      neutral: 'text-muted-foreground',
    },
  },
  defaultVariants: { trend: 'neutral' },
});

export type StatCardDeltaRecipeProps = VariantProps<typeof statCardDeltaRecipe>;

/**
 * STAT PAIR — a section of the phone's Home: a muted heading with its glyph,
 * then one or two headline numbers side by side with a rule between them.
 * No card, as the Figma's Home draws it (143:7710): the sections sit on the
 * page and are ruled off from each other, because cards on a phone are
 * mostly border.
 *
 * 16 above the heading and 32 below the numbers: the Figma's card pads 16 at
 * each end and its sections are a further 16 apart, with the rule at the
 * top of the next, so a stack of these ruled off with `divided` lands each
 * rule 32 under the numbers above it and 16 over the heading below it.
 */
export const statPairRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'flex flex-col gap-3 pb-8 pt-4',
]);

export const statPairFiguresRecipe = cva([
  uiMarker,
  uiMarkerLayout,
  'grid grid-cols-[1fr_auto_1fr] items-stretch gap-4',
]);
